/**
 * Filtres à facettes pilotés par le back (routes {domaine}/liste/filtres).
 *
 * Le state de filtres du TableContext est indexé par facet.key (statuses, products,
 * plateformes, brokers, period…) ; ces helpers convertissent ce state générique vers
 * le FiltersDto attendu par les routes de liste, et le sorting colonne -> enum numérique.
 */

/** Une option de facette (structurellement identique aux FacetValueDto générés). */
export interface FacetValue {
  id: string;
  label: string;
  count: number;
}

/** Une dimension de filtre (structurellement identique aux FacetDto générés). */
export interface Facet {
  key: string;
  label: string;
  type: string; // 'multiselect' | 'daterange' | futurs types
  values: FacetValue[];
}

/**
 * Filtre booléen (case à cocher) non piloté par le back : rendu par le drawer en plus
 * des facettes du référentiel. La valeur vit dans le state de filtres du TableContext
 * (true/false, jamais retirée pour un tri-état comme isTest par défaut à false).
 */
export interface BooleanFacet {
  key: string;
  label: string;
  /** Texte d'aide sous la case (ex. « Afficher uniquement les prélèvements de test »). */
  hint?: string;
}

/**
 * Case « Inclure tests » commune à toutes les listes admin. Décochée (défaut) : l'orga de
 * test (Confiancia Testing) est exclue de la liste, du totalCount et des facettes. Cochée :
 * `includeTests: true` remonte tout. On n'envoie rien tant qu'elle n'est pas touchée.
 */
export const INCLUDE_TESTS_BOOLEAN_FACET: BooleanFacet[] = [
  {
    key: 'includeTests',
    label: 'Inclure tests',
    hint: "Afficher aussi la plateforme de test (Confiancia Testing), exclue par défaut"
  }
];

/** Période sélectionnée dans une facette daterange (dates ISO yyyy-mm-dd). */
export interface FacetPeriod {
  startDate?: string;
  endDate?: string;
}

/** Body commun des routes liste et liste/filtres (structurellement = GetGenericListCommandDto). */
export interface FacetQueryCommand {
  pageNumber: number;
  pageSize: number;
  searchString?: string;
  filters?: Record<string, any>;
}

/**
 * Mémoire partagée entre le drawer et les chips de filtres actifs :
 * libellés des options (id -> label) et des dimensions (facet.key -> label).
 */
export interface FacetLabelCache {
  values: Map<string, string>;
  facets: Map<string, string>;
}

export const createFacetLabelCache = (): FacetLabelCache => ({
  values: new Map(),
  facets: new Map()
});

/** Libellés de secours des dimensions connues, si la facette n'a pas encore été chargée. */
export const DEFAULT_FACET_LABELS: Record<string, string> = {
  statuses: 'Statut',
  branches: 'Branche',
  products: 'Produit',
  tunnels: 'Tunnel',
  owners: 'Assureur',
  // Clés = codes de facettes back (Codes.Facettes) — valeurs wire conservées.
  organizations: 'Plateforme',
  partnerOrganizations: 'Partenaire apporteur',
  brokers: 'Courtier',
  courtiers: 'Courtier',
  groupContracts: 'Contrat groupe',
  persons: 'Client',
  personTypes: 'Type de personne',
  categories: 'Catégorie',
  simulationSource: 'Provenance',
  active: 'Statut',
  withdrawalModes: 'Mode de prélèvement',
  includeTests: 'Inclure tests',
  period: 'Période'
};

/** Mapping facet.key -> champ du FiltersDto (consigne back). Clé inconnue = passée telle quelle. */
const FACET_KEY_TO_FILTER_FIELD: Record<string, string> = {
  statuses: 'statuses',
  branches: 'branchIds',
  products: 'productIds',
  tunnels: 'tunnelIds',
  owners: 'owners',
  plateformes: 'plateformeIds',
  // Leads externes : partenaire apporteur (≠ plateforme traitante)
  partnerPlateformes: 'partnerPlateformeIds',
  brokers: 'brokerIds',
  // Vue partagée (rôle Plateforme / portefeuille partagé) : facette back « courtiers »
  // (identique à l'admin) -> filtre brokerIds sur les routes courtier.
  courtiers: 'brokerIds',
  groupContracts: 'groupContractIds',
  persons: 'personIds',
  personTypes: 'personTypeIds',
  categories: 'categoryIds',
  simulationSource: 'sourceIds',
  active: 'active',
  withdrawalModes: 'withdrawalModes',
  includeTests: 'includeTests'
};

/**
 * Convertit le state de filtres du tableau (indexé par facet.key) vers le FiltersDto
 * des routes de liste. Renvoie undefined si aucun filtre actif.
 */
export function mapFacetFiltersToFiltersDto(
  tableFilters: Record<string, any> | undefined
): Record<string, any> | undefined {
  if (!tableFilters) return undefined;
  const dto: Record<string, any> = {};
  for (const [key, value] of Object.entries(tableFilters)) {
    if (key === 'period') {
      const period = value as FacetPeriod | undefined;
      if (period?.startDate || period?.endDate) {
        // Dates CALENDAIRES (contrat back « Dates & fuseaux ») : string "yyyy-MM-dd"
        // de bout en bout — jamais d'objet Date sérialisé (décalage de fuseau).
        dto.period = {
          startDate: period.startDate || undefined,
          endDate: period.endDate || undefined
        };
      }
    } else if (typeof value === 'boolean') {
      // Filtres booléens (ex. isTest tri-état) : la valeur false est signifiante
      // (exclure) et doit être transmise telle quelle, pas seulement quand true.
      dto[FACET_KEY_TO_FILTER_FIELD[key] ?? key] = value;
    } else if (Array.isArray(value) && value.length > 0) {
      dto[FACET_KEY_TO_FILTER_FIELD[key] ?? key] = value;
    }
  }
  return Object.keys(dto).length > 0 ? dto : undefined;
}

// Le tri passe désormais par sortBy/sortDirection dans le body (l'enum numérique
// legacy `sorting` est ignoré par le back dès que sortBy est renseigné).
